import {
  IonContent,
  IonPage,
  IonButton,
  IonCard,
  IonCardContent,
} from '@ionic/react';
import { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { useWallet } from '../contexts/WalletContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { playCelebrationSound } from '../utils/soundUtils';
import './Game2048.css';

type Grid = number[][];

const Game2048: React.FC = () => {
  const { earnCoins } = useWallet();
  const [grid, setGrid] = useState<Grid>([]);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const GRID_SIZE = 4;
  const TARGET = 2048;

  useEffect(() => {
    const savedBest = localStorage.getItem('2048-best-score');
    if (savedBest) {
      setBestScore(parseInt(savedBest));
    }
    initializeGame();
  }, []);

  const initializeGame = () => {
    const newGrid: Grid = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0));
    addRandomTile(newGrid);
    addRandomTile(newGrid);
    setGrid(newGrid);
    setScore(0);
    setGameOver(false);
    setWon(false);
  };

  const addRandomTile = (grid: Grid) => {
    const emptyCells: [number, number][] = [];
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        if (grid[i][j] === 0) {
          emptyCells.push([i, j]);
        }
      }
    }
    if (emptyCells.length > 0) {
      const [row, col] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
      grid[row][col] = Math.random() < 0.9 ? 2 : 4;
    }
  };

  const moveLeft = (grid: Grid): { newGrid: Grid; moved: boolean; points: number } => {
    const newGrid: Grid = grid.map(row => [...row]);
    let moved = false;
    let points = 0;

    for (let i = 0; i < GRID_SIZE; i++) {
      const row = newGrid[i].filter(val => val !== 0);
      const newRow: number[] = [];
      let j = 0;

      while (j < row.length) {
        if (j < row.length - 1 && row[j] === row[j + 1]) {
          const merged = row[j] * 2;
          newRow.push(merged);
          points += merged;
          j += 2;
        } else {
          newRow.push(row[j]);
          j++;
        }
      }

      while (newRow.length < GRID_SIZE) {
        newRow.push(0);
      }

      if (JSON.stringify(newRow) !== JSON.stringify(newGrid[i])) {
        moved = true;
      }
      newGrid[i] = newRow;
    }

    return { newGrid, moved, points };
  };

  const rotateGrid = (grid: Grid): Grid => {
    const newGrid: Grid = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0));
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        newGrid[j][GRID_SIZE - 1 - i] = grid[i][j];
      }
    }
    return newGrid;
  };

  const move = (direction: 'left' | 'right' | 'up' | 'down') => {
    if (gameOver) return;

    let newGrid = grid.map(row => [...row]);
    let moved = false;
    let points = 0;

    // Rotate to always work with left movement
    if (direction === 'right') {
      newGrid = rotateGrid(rotateGrid(newGrid));
    } else if (direction === 'up') {
      newGrid = rotateGrid(rotateGrid(rotateGrid(newGrid)));
    } else if (direction === 'down') {
      newGrid = rotateGrid(newGrid);
    }

    const result = moveLeft(newGrid);
    newGrid = result.newGrid;
    moved = result.moved;
    points = result.points;

    // Rotate back
    if (direction === 'right') {
      newGrid = rotateGrid(rotateGrid(newGrid));
    } else if (direction === 'up') {
      newGrid = rotateGrid(newGrid);
    } else if (direction === 'down') {
      newGrid = rotateGrid(rotateGrid(rotateGrid(newGrid)));
    }

    if (moved) {
      addRandomTile(newGrid);
      const newScore = score + points;
      setScore(newScore);
      if (newScore > bestScore) {
        setBestScore(newScore);
        localStorage.setItem('2048-best-score', newScore.toString());
      }

      // Check for win
      if (!won && newGrid.some(row => row.some(cell => cell === TARGET))) {
        setWon(true);
        handleWin();
      }

      // Check for game over
      if (isGameOver(newGrid)) {
        handleGameOver();
      }

      setGrid(newGrid);
    }
  };

  const isGameOver = (grid: Grid): boolean => {
    // Check for empty cells
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        if (grid[i][j] === 0) return false;
      }
    }

    // Check for possible merges
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        const current = grid[i][j];
        if (
          (i < GRID_SIZE - 1 && grid[i + 1][j] === current) ||
          (j < GRID_SIZE - 1 && grid[i][j + 1] === current)
        ) {
          return false;
        }
      }
    }

    return true;
  };

  const handleWin = async () => {
    playCelebrationSound();
    
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: NodeJS.Timeout = setInterval(function() {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    // Award coins for winning
    const coins = 200 + Math.floor(score / 50);
    try {
      await earnCoins(coins, `2048 Puzzle - Reached ${TARGET}! Score: ${score}`, 'game-2048');
    } catch (error) {
      console.error('Error awarding coins:', error);
    }
  };

  const handleGameOver = async () => {
    setGameOver(true);
    // Only award coins if score is at least 100 (so minimum 51 coins)
    const coins = score >= 100 ? 50 + Math.floor(score / 100) : 0;
    if (coins > 0) {
      try {
        await earnCoins(coins, `2048 Puzzle - Score: ${score}`, 'game-2048');
      } catch (error) {
        console.error('Error awarding coins:', error);
      }
    }
  };

  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const direction = e.key.replace('Arrow', '').toLowerCase() as 'left' | 'right' | 'up' | 'down';
      move(direction);
    }
  }, [grid, gameOver]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  const handleSwipe = (direction: 'left' | 'right' | 'up' | 'down') => {
    move(direction);
  };


  return (
    <IonPage>
      <AppHeader title="2048 Puzzle" showBackButton={true} defaultHref="/games" />
      <IonContent fullscreen className="ion-padding">
        <div className="game2048-container">
          <IonCard className="score-card">
            <IonCardContent>
              <div className="score-row">
                <div className="score-item">
                  <div className="score-label">Score</div>
                  <div className="score-value">{score}</div>
                </div>
                <div className="score-item">
                  <div className="score-label">Best</div>
                  <div className="score-value">{bestScore}</div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          <div className="game-board">
            <div className="grid-container">
              {grid.map((row, i) =>
                row.map((cell, j) => (
                  <div key={`${i}-${j}`} className="grid-cell">
                    {cell !== 0 && (
                      <div
                        className="tile"
                        data-value={cell}
                      >
                        {cell}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="game-controls">
            <div className="control-buttons">
              <IonButton onClick={() => handleSwipe('up')} disabled={gameOver}>
                ↑
              </IonButton>
              <div className="horizontal-controls">
                <IonButton onClick={() => handleSwipe('left')} disabled={gameOver}>
                  ←
                </IonButton>
                <IonButton onClick={() => handleSwipe('down')} disabled={gameOver}>
                  ↓
                </IonButton>
                <IonButton onClick={() => handleSwipe('right')} disabled={gameOver}>
                  →
                </IonButton>
              </div>
            </div>
            <IonButton expand="block" color="primary" onClick={initializeGame}>
              New Game
            </IonButton>
          </div>

          {won && !gameOver && (
            <IonCard className="win-card">
              <IonCardContent>
                <h2>🎉 You Win!</h2>
                <p>You reached {TARGET}!</p>
                <IonButton expand="block" onClick={() => setWon(false)}>
                  Continue Playing
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {gameOver && !won && (
            <IonCard className="game-over-card">
              <IonCardContent>
                <h2>Game Over</h2>
                <p>Final Score: {score}</p>
                <IonButton expand="block" color="primary" onClick={initializeGame}>
                  Try Again
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          <IonCard className="info-card">
            <IonCardContent>
              <p><strong>How to Play:</strong></p>
              <p>Use arrow keys or buttons to move tiles. When two tiles with the same number touch, they merge into one!</p>
              <p><strong>Goal:</strong> Reach {TARGET} to win!</p>
              <p className="coin-info">💰 Coins awarded based on final score!</p>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default Game2048;

